/**
 * Página de Recuperação de Senha
 *
 * Tela para solicitar reset de senha via email.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Spinner } from '@components/ui/Spinner';
import { api } from '@services/api';
import { apiErrorMessage } from '@utils/helpers';
import toast from 'react-hot-toast';
import { logoSquareWhite, logoSquarePrimary } from '@assets/index';

/**
 * Schema de validação
 */
const forgotPasswordSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

/**
 * Componente de página de Recuperação de Senha
 */
export function ForgotPassword() {
  // Estado de loading
  const [isLoading, setIsLoading] = useState(false);
  // Estado de sucesso
  const [isSuccess, setIsSuccess] = useState(false);
  const [searchParams] = useSearchParams();

  // Configuração do formulário
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    // Vindo do cadastro com e-mail já cadastrado (#289): e-mail preenchido
    defaultValues: { email: searchParams.get('email') || '' },
  });

  /**
   * Handler de submit do formulário
   */
  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      await api.post('/auth/forgot-password', data);
      setIsSuccess(true);
      toast.success('Email de recuperação enviado!');
    } catch (error) {
      // O backend já responde sucesso quando o e-mail não existe (por segurança); um erro
      // aqui é falha de verdade (sem internet, limite de tentativas, servidor fora) e não
      // pode virar "e-mail enviado" (#289)
      toast.error(
        apiErrorMessage(error, 'Não foi possível enviar o e-mail agora. Tente de novo em alguns minutos.'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Tela de sucesso
  if (isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success-100 dark:bg-success-900/30">
            <CheckCircle size={40} className="text-success-700" />
          </div>

          <h1 className="text-2xl font-bold text-primary-900 dark:text-white">
            Email enviado!
          </h1>

          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Se existe uma conta com o email informado, você receberá um link
            para redefinir sua senha em alguns minutos.
          </p>

          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Não se esqueça de verificar a pasta de spam.
          </p>

          <Link to="/entrar" className="btn btn-primary mt-8 inline-flex">
            <ArrowLeft size={20} />
            Voltar para login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img
            src={logoSquarePrimary}
            alt="VendeAi"
            className="h-20 w-20 rounded-2xl object-contain shadow-lg dark:hidden"
          />
          <img
            src={logoSquareWhite}
            alt="VendeAi"
            className="hidden h-20 w-20 rounded-2xl object-contain shadow-lg dark:block"
          />
        </div>

        {/* Título */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-primary-900 dark:text-white">
            Esqueceu sua senha?
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Informe seu email e enviaremos um link para redefinir sua senha.
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Campo Email */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="seu@email.com"
              {...register('email')}
              className={errors.email ? 'border-error-500' : ''}
            />
            {errors.email && (
              <span className="form-error">{errors.email.message}</span>
            )}
          </div>

          {/* Botão de submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? (
              <Spinner size="sm" color="white" />
            ) : (
              <>
                <Mail size={20} />
                Enviar email de recuperação
              </>
            )}
          </button>
        </form>

        {/* Link para voltar */}
        <p className="mt-8 text-center">
          <Link
            to="/entrar"
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft size={16} />
            Voltar para login
          </Link>
        </p>
      </div>
    </div>
  );
}
